import React, { useState } from 'react';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentTextIcon, CircleStackIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import {
  exportTransactionsToCSV,
  exportGoalsToCSV,
  exportSnapshotsToCSV,
  exportAllDataToJSON,
  downloadCSV,
  downloadJSON,
  importTransactionsFromCSV,
  getSampleTransactionsCSV,
  getSamplePortfolioUpdateCSV,
  importTransactionsWithAllocationsFromCSV,
  getSampleTransactionsWithAllocationsCSV,
  exportTransactionsInflowOutflowCSV,
  importTransactionsInflowOutflowCSV,
  getSampleInflowOutflowCSV,
} from '../../utils/csvHelpers';
import dayjs from 'dayjs';

const ImportExport = () => {
  const {
    portfolios,
    goals,
    transactions,
    snapshots,
    fundPrices,
    exchangeRates,
    settings,
    properties,
    mortgages,
    proposedTransactions,
    schoolFeePlans,
    importTransactions,
    importAllData,
  } = useData();

  const [importing, setImporting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [includeSnapshots, setIncludeSnapshots] = useState(false);
  const [includeHistoricalPrices, setIncludeHistoricalPrices] = useState(false);

  // Export functions
  const handleExportTransactions = () => {
    const csv = exportTransactionsToCSV(transactions, goals);
    downloadCSV(csv, `transactions-${dayjs().format('YYYY-MM-DD')}.csv`);
  };

  const handleExportTransactionsInflowOutflow = () => {
    const csv = exportTransactionsInflowOutflowCSV(transactions, portfolios, []);
    downloadCSV(csv, `transactions-inflow-outflow-${dayjs().format('YYYY-MM-DD')}.csv`);
  };

  const handleExportGoals = () => {
    const totalValue = portfolios.reduce((sum, p) => sum + p.currentValue, 0);
    const csv = exportGoalsToCSV(goals, totalValue);
    downloadCSV(csv, `goals-${dayjs().format('YYYY-MM-DD')}.csv`);
  };

  const handleExportSnapshots = () => {
    const csv = exportSnapshotsToCSV(snapshots);
    downloadCSV(csv, `portfolio-history-${dayjs().format('YYYY-MM-DD')}.csv`);
  };

  const handleExportAllData = () => {
    const exportedFundPrices = includeHistoricalPrices
      ? fundPrices
      : fundPrices.map(({ priceHistory, ...rest }) => rest);

    const allData = {
      portfolios,
      goals,
      transactions,
      ...(includeSnapshots && { snapshots }),
      fundPrices: exportedFundPrices,
      exchangeRates,
      settings,
      properties,
      mortgages,
      proposedTransactions,
      schoolFeePlans,
      exportDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    };
    const json = exportAllDataToJSON(allData);
    downloadJSON(json, `portfolio-backup-${dayjs().format('YYYY-MM-DD')}.json`);
  };

  const handleDownloadSampleTransactions = () => {
    const csv = getSampleTransactionsCSV();
    downloadCSV(csv, 'sample-transactions.csv');
  };

  const handleDownloadSamplePortfolioUpdate = () => {
    const csv = getSamplePortfolioUpdateCSV();
    downloadCSV(csv, 'sample-portfolio-update.csv');
  };

  const handleDownloadSampleTransactionsWithAllocations = () => {
    const csv = getSampleTransactionsWithAllocationsCSV();
    downloadCSV(csv, 'sample-transactions-with-allocations.csv');
  };

  const handleDownloadSampleInflowOutflow = () => {
    const csv = getSampleInflowOutflowCSV();
    downloadCSV(csv, 'sample-inflow-outflow.csv');
  };

  // Import functions
  const handleImportTransactions = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        // Use the first portfolio if there are any, otherwise null
        const defaultPortfolioId = portfolios.length > 0 ? portfolios[0].id : null;
        const newTransactions = importTransactionsFromCSV(content, defaultPortfolioId, goals);

        if (newTransactions.length === 0) {
          setFeedbackMessage({ type: 'warning', text: 'No valid transactions found in file' });
        } else {
          importTransactions(newTransactions);
          setFeedbackMessage({ type: 'success', text: `Successfully imported ${newTransactions.length} transaction(s)` });
        }
      } catch (error) {
        setFeedbackMessage({ type: 'error', text: 'Error importing transactions: ' + error.message });
        console.error(error);
      } finally {
        setImporting(false);
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      setFeedbackMessage({ type: 'error', text: 'Error reading file' });
      setImporting(false);
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const handleImportTransactionsWithAllocations = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const newTransactions = importTransactionsWithAllocationsFromCSV(content, portfolios, []);

        if (newTransactions.length === 0) {
          setFeedbackMessage({ type: 'warning', text: 'No valid transactions found in file' });
        } else {
          importTransactions(newTransactions);
          setFeedbackMessage({ type: 'success', text: `Successfully imported ${newTransactions.length} transaction(s)` });
        }
      } catch (error) {
        setFeedbackMessage({ type: 'error', text: 'Error importing transactions: ' + error.message });
        console.error(error);
      } finally {
        setImporting(false);
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      setFeedbackMessage({ type: 'error', text: 'Error reading file' });
      setImporting(false);
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const handleImportTransactionsInflowOutflow = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const content = e.target.result;
        const newTransactions = await importTransactionsInflowOutflowCSV(
          content,
          portfolios,
          []
        );

        if (newTransactions.length === 0) {
          setFeedbackMessage({ type: 'warning', text: 'No valid transactions found in file' });
        } else {
          await importTransactions(newTransactions);
          setFeedbackMessage({ 
            type: 'success', 
            text: `Successfully imported ${newTransactions.length} transaction(s) from Inflow/Outflow format` 
          });
        }
      } catch (error) {
        setFeedbackMessage({ 
          type: 'error', 
          text: 'Error importing transactions: ' + (error.message || 'Unknown error') 
        });
        console.error('Import error:', error);
      } finally {
        setImporting(false);
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      setFeedbackMessage({ type: 'error', text: 'Error reading file' });
      setImporting(false);
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const handleImportAllData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const data = JSON.parse(content);

        importAllData(data);
        setFeedbackMessage({ type: 'success', text: 'Successfully restored backup data' });
      } catch (error) {
        setFeedbackMessage({ type: 'error', text: 'Error importing data: ' + error.message });
        console.error(error);
      } finally {
        setImporting(false);
        event.target.value = '';
      }
    };

    reader.onerror = () => {
      setFeedbackMessage({ type: 'error', text: 'Error reading file' });
      setImporting(false);
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold text-gray-900">Import & Export Data</h1>

      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 mb-6">
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-gray-900">Data Safety</p>
          <p className="text-gray-700">
            All your data is stored locally in your browser. Use export to create backups, and import to restore or migrate data.
          </p>
        </div>
      </div>

      {feedbackMessage && (
        <div className={`rounded-lg border p-4 mb-6 ${
          feedbackMessage.type === 'success' ? 'bg-green-50 border-green-200' :
          feedbackMessage.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
        }`}>
          <p className="text-gray-900">{feedbackMessage.text}</p>
        </div>
      )}

      {/* Export Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-2 items-center">
            <ArrowDownTrayIcon className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
          </div>

          <p className="text-gray-700">
            Export your data to CSV or JSON format for backup, analysis, or migration.
          </p>

          <div className="flex flex-col gap-6">
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Export Individual Data</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleExportTransactions}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Export Transactions (Simple CSV)</span>
                </button>
                <button
                  onClick={handleExportTransactionsInflowOutflow}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Export Inflow/Outflow Format (CSV)</span>
                </button>
                <button
                  onClick={handleExportGoals}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Export Funds (CSV)</span>
                </button>
                <button
                  onClick={handleExportSnapshots}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Export Portfolio History (CSV)</span>
                </button>
              </div>
            </div>

            <hr className="border-gray-300" />

            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Full Backup</h4>
              <div className="flex flex-col gap-2 mb-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeSnapshots}
                    onChange={(e) => setIncludeSnapshots(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Include portfolio snapshots (historical value data)
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeHistoricalPrices}
                    onChange={(e) => setIncludeHistoricalPrices(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Include historical fund prices
                </label>
              </div>
              <button
                onClick={handleExportAllData}
                className="flex items-center gap-2 px-4 py-2  text-white rounded-lg  transition-colors"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
              >
                <CircleStackIcon className="w-5 h-5" />
                <span>Export All Data (JSON)</span>
              </button>
              <p className="mt-2 text-sm text-gray-600">
                Creates a complete backup of all your data: portfolios, funds, transactions, fund prices, exchange rates, settings, properties, mortgages, proposed contributions, and school fee plans.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-2 items-center">
            <ArrowUpTrayIcon className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900">Import Data</h3>
          </div>

          <p className="text-gray-700">
            Import transactions or restore a complete backup from a JSON file.
          </p>

          <div className="flex flex-col gap-6">
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Import Transactions (Simple Format)</h4>
              <input
                type="file"
                accept=".csv"
                onChange={handleImportTransactions}
                disabled={importing}
                style={{ display: 'none' }}
                id="import-transactions-input"
              />
              <button
                onClick={() => document.getElementById('import-transactions-input').click()}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2  text-white rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
              >
                <ArrowUpTrayIcon className="w-5 h-5" />
                <span>{importing ? 'Uploading...' : 'Upload Transactions CSV'}</span>
              </button>
              <p className="mt-2 text-sm text-gray-600">
                CSV format: Date, Type, Amount, Goal, Description
              </p>
            </div>

            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Import Inflow/Outflow Format</h4>
              <input
                type="file"
                accept=".csv"
                onChange={handleImportTransactionsInflowOutflow}
                disabled={importing}
                style={{ display: 'none' }}
                id="import-transactions-inflow-outflow-input"
              />
              <button
                onClick={() => document.getElementById('import-transactions-inflow-outflow-input').click()}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2  text-white rounded-lg  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#012E58' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
              >
                <ArrowUpTrayIcon className="w-5 h-5" />
                <span>{importing ? 'Uploading...' : 'Upload Inflow/Outflow CSV'}</span>
              </button>
              <p className="mt-2 text-sm text-gray-600">
                CSV format: Account, Date, Payee, Category (Pot), Memo, Outflow, Inflow
              </p>
            </div>

            <hr className="border-gray-300" />

            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3">Restore Full Backup</h4>
              <input
                type="file"
                accept=".json"
                onChange={handleImportAllData}
                disabled={importing}
                style={{ display: 'none' }}
                id="import-all-data-input"
              />
              <button
                onClick={() => document.getElementById('import-all-data-input').click()}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowUpTrayIcon className="w-5 h-5" />
                <span>{importing ? 'Uploading...' : 'Upload Backup JSON (Will overwrite all data)'}</span>
              </button>
              <p className="mt-2 text-sm text-gray-600">
                Warning: This will replace all existing data with the backup.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sample Files */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Sample CSV Templates</h3>
          <p className="text-gray-700">
            Download sample CSV files to see the expected format for imports.
          </p>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleDownloadSampleTransactions}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Sample Transactions (Simple)</span>
            </button>
            <button
              onClick={handleDownloadSampleInflowOutflow}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Sample Inflow/Outflow Format</span>
            </button>
            <button
              onClick={handleDownloadSamplePortfolioUpdate}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>Sample Portfolio Update CSV</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExport;
